import React, { useState } from 'react'
import { Badge, Button, Card, CardBody, CardSubtitle, Col, Row, Spinner } from 'reactstrap'
// The variable table the per-location config screens use, as it is: the same columns, the same info
// tooltip, the same pencil opening VariableForm with its value builder.
import VarData from 'pages/OpenDockLocConfig/VarData'
import { byWarehouse, countWarehouses, toDecisions, UNRESOLVED } from './rows'

// The status words, their colours and their explanations all come from the endpoint, so renaming a
// status is a backend change and this screen follows. One place resolving a status, so the badge
// and the collapsed summary can never word the same status differently.
function statusWord(status, statuses) {
    return (statuses || {})[status] || { label: status, tone: 'secondary', note: '' }
}

function buildStatusColumn(statuses) {
    return {
        header: 'Status',
        render: entry => {
            const word = statusWord(entry.status || UNRESOLVED, statuses)
            return <Badge color={word.tone} title={word.note}>{word.label}</Badge>
        },
    }
}

// Our rows and a config variable are the same thing under different names: the facility's wording
// is the label, the var it fills is the name, and what the run could not decide becomes the notes.
function toVars(rows) {
    return rows.map(row => ({
        label: row.question,
        name: row.var_name,
        type: row.proposed_type,
        value: row.proposed_value,
        required: !!row.required,
        status: row.status,
        information: row.options || [],
        notes: row.notes || '',
    }))
}

// What a collapsed warehouse says about itself, in whatever statuses its rows actually carry.
function summariseStatuses(rows, statuses) {
    const countsByStatus = new Map()
    rows.forEach(row => {
        const status = row.status || UNRESOLVED
        countsByStatus.set(status, (countsByStatus.get(status) || 0) + 1)
    })
    return [...countsByStatus]
        .map(([status, count]) => `${count} ${statusWord(status, statuses).label}`)
        .join(', ')
}

function WarehouseVars({ group, onChange, statuses }) {
    // A warehouse with nothing left to answer starts closed: there are dozens of them. A sheet off
    // disk carries no status on an unanswered row, so an empty one counts as unresolved.
    const [open, setOpen] = useState(
        group.rows.some(row => (row.status || UNRESOLVED) === UNRESOLVED))

    // VarData hands back the whole list. A setting cannot be added or removed here, it is what the
    // facility asks for, so a row missing from the list means its answer was cleared. Nullish
    // coalescing, not ||, so a deliberate 0 or false survives as an answer.
    const setVars = next => {
        const entriesByName = new Map((next || []).map(entry => [entry.name, entry]))
        group.rows.forEach(row => {
            const entry = entriesByName.get(row.var_name)
            onChange(row.id, {
                proposed_type: (entry && entry.type) ?? '',
                proposed_value: (entry && entry.value) ?? '',
            })
        })
    }

    return (
        <Card>
            <CardBody>
                <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setOpen(!open)}
                >
                    <h6 className="mb-0">
                        <span className="me-2 text-muted">{open ? '▾' : '▸'}</span>
                        {group.location}{' '}
                        <span className="text-muted fw-normal">({group.warehouseId})</span>
                    </h6>
                    <span className="text-muted small">
                        {summariseStatuses(group.rows, statuses)}
                    </span>
                </div>
                {open && (
                    <div className="mt-3">
                        {/* isBase hides Add: the settings are the facility's, not ours to invent */}
                        <VarData
                            vars={toVars(group.rows)}
                            labelWidth={2}
                            setVars={setVars}
                            extraColumn={buildStatusColumn(statuses)}
                            isBase
                            isEdit
                        />
                    </div>
                )}
            </CardBody>
        </Card>
    )
}

// The sheets a run leaves behind, each its own download: the questions to put to the customer, and
// the assumed values to confirm. Separate files, because confirming an assumption is its own pass.
function SheetLink({ file, children }) {
    if (!file || !file.downloadUrl) return null
    return (
        <a
            className="btn btn-sm btn-link"
            href={file.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
        >
            {children}
        </a>
    )
}

function DecisionsReview({
    rows, onChangeRow, onApply, applying, onBack, onStartOver, questionsFile, assumedFile, vocabulary,
}) {
    const decisions = toDecisions(rows)

    if (!rows.length) {
        return (
            <Card>
                <CardBody className="text-center py-5">
                    <h5 className="mb-2">No unresolved settings</h5>
                    <p className="text-muted mb-4">
                        All settings were determined from booking history and written.
                    </p>
                    <Button color="primary" onClick={onStartOver}>Onboard another account</Button>
                </CardBody>
            </Card>
        )
    }

    return (
        <>
            <Card className="p-3">
                <Row>
                    <Col>
                        <span className="text-primary text-size-16 m-2">Warehouse settings</span>
                    </Col>
                    <Col className="text-end">
                        <SheetLink file={questionsFile}>Questions sheet</SheetLink>
                        <SheetLink file={assumedFile}>Assumed sheet</SheetLink>
                        <Button size="sm" color="link" className="text-muted" onClick={onBack}>
                            Back
                        </Button>
                    </Col>
                </Row>
                <CardSubtitle className="m-2 text-muted">
                    {rows.length} settings across {countWarehouses(rows)} warehouses.
                    Unresolved settings are blank — enter a type and value to configure them.
                    Settings left blank are not written.
                </CardSubtitle>
            </Card>

            {byWarehouse(rows).map(group => (
                <WarehouseVars
                    key={group.warehouseId}
                    group={group}
                    onChange={onChangeRow}
                    statuses={vocabulary && vocabulary.statuses}
                />
            ))}

            <div className="modal-footer">
                <span className="text-muted small me-auto">
                    {decisions.length} of {rows.length} settings will be written.
                </span>
                <Button
                    color="primary"
                    onClick={() => onApply(decisions)}
                    disabled={applying || !decisions.length}
                    className="m-2"
                >
                    {applying && <Spinner size="sm" className="me-2" />}
                    {applying ? 'Applying…' : 'Apply'}
                </Button>
            </div>
        </>
    )
}

export default DecisionsReview
